// Landing / Home page.
// Hero + featured tournaments + prominent "Record Game" sandbox CTA.
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Activity, ArrowRight, MapPin, Calendar, Trophy } from "lucide-react";

export default function Home() {
  const [tournaments, setTournaments] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get("/tournaments?status=approved")
      .then(r => { if (alive) setTournaments(r.data || []); })
      .catch(err => console.warn("[home] failed to load tournaments", err));
    return () => { alive = false; };
  }, []);

  return (
    <div className="text-white">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: "url('https://images.pexels.com/photos/20615486/pexels-photo-20615486.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/80 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-32 grid lg:grid-cols-[1.2fr_1fr] gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.3em] uppercase text-court mb-6 border border-court/40 px-3 py-1">
              <Activity className="w-3 h-3" /> Live Scoring Platform
            </div>
            <h1 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
              Every Bucket.<br />
              <span className="text-court">Every Stat.</span><br />
              Every Game.
            </h1>
            <p className="mt-6 max-w-xl text-zinc-300 text-base sm:text-lg">
              Court Matiks is the tap-and-play scoring engine for sideline statisticians,
              with a public tournament directory and lifetime portfolios for every player.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/sandbox/score"
                data-testid="home-record-game-btn"
                className="px-6 h-14 bg-court text-white font-display font-black tracking-wider uppercase text-lg border-b-4 border-court-deep hover:bg-court-hover active:translate-y-0.5 active:border-b-0 transition-all inline-flex items-center gap-2"
              >
                <Activity className="w-5 h-5" /> Record A Game
              </Link>
              <Link
                to="/tournaments"
                data-testid="home-browse-tournaments-btn"
                className="px-6 h-14 border border-zinc-700 font-display font-black tracking-wider uppercase text-lg hover:border-white inline-flex items-center gap-2"
              >
                <Trophy className="w-5 h-5" /> Browse Tournaments
              </Link>
            </div>
            <div className="mt-6 text-xs font-mono text-zinc-500">
              Find a player, uncover their story. Underrated, overrated, legend or hype—you decide. We just deliver the raw, immutable history.
            </div>
          </div>

          {/* Statline panel */}
          <div className="hidden lg:block">
            <div className="bg-black/60 backdrop-blur-xl border border-white/10 p-6 grid grid-cols-3 gap-6">
              {[
                ["PTS", "108"],
                ["AST", "24"],
                ["REB", "47"],
                ["STL", "12"],
                ["BLK", "5"],
                ["FG%", ".512"],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">{k}</div>
                  <div className="font-display font-black text-4xl tracking-tighter">{v}</div>
                </div>
              ))}
              <div className="col-span-3 pt-2 border-t border-white/10 text-[10px] tracking-widest uppercase text-zinc-500">
                Live boxscore · Q3 8:42 · Court Kings vs Skyline Suns
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEEDBACK PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="border border-zinc-800 p-6 rounded-lg flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-black/40">
          <div>
            <div className="text-xs font-bold tracking-widest uppercase text-zinc-400">Feedback — Beta</div>
            <h3 className="font-display font-black text-2xl mt-1">Help shape Court Matiks</h3>
            <p className="mt-2 text-sm text-zinc-400">We're in beta — a quick 2‑minute form helps us prioritize improvements. Click the button to open the feedback form.</p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="https://forms.gle/BAAxGbxbHnU9AjAD9"
              target="_blank"
              rel="noreferrer"
              data-testid="home-feedback-cta"
              className="px-5 py-3 bg-court text-black font-bold uppercase tracking-widest rounded-md hover:opacity-95"
            >
              Give Feedback
            </a>
          </div>
        </div>
      </section>

      {/* PERSONAS */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { t: "Players", d: "Phone-based identity. A career portfolio of every game, every team, every stat — yours forever.", to: "/login", cta: "Claim your portfolio" },
            { t: "Organizers", d: "List tournaments in minutes. Approved listings appear in the public directory for thousands of athletes.", to: "/tournaments/new", cta: "Host a tournament" },
            { t: "Statisticians", d: "Mobile-first tap-and-play scoring. Shot charts, fouls, timeouts — all in your thumbs.", to: "/sandbox/score", cta: "Try the engine" },
          ].map(p => (
            <div key={p.t} className="border border-zinc-800 hover:border-zinc-600 p-6 transition-colors">
              <h3 className="font-display font-black text-2xl uppercase tracking-tight">{p.t}</h3>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{p.d}</p>
              <Link to={p.to} className="mt-4 inline-flex items-center gap-1 text-court text-xs font-bold tracking-widest uppercase hover:gap-2 transition-all">
                {p.cta} <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED TOURNAMENTS */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display font-black text-3xl uppercase tracking-tight">Featured Tournaments</h2>
          <Link to="/tournaments" data-testid="home-view-all-tournaments" className="text-xs font-bold tracking-widest uppercase text-zinc-400 hover:text-white">View all →</Link>
        </div>
        {tournaments.length === 0 ? (
          <div className="border border-zinc-800 p-8 text-zinc-500 text-sm">No tournaments yet. Run <span className="font-mono text-court">python seed.py</span> on the backend to populate samples.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tournaments.slice(0, 6).map(t => (
              <Link key={t.id} to="/tournaments" data-testid={`home-tournament-card-${t.id}`} className="border border-zinc-800 hover:border-court p-5 group transition-colors block">
                <div className="text-[10px] tracking-[0.25em] uppercase text-court">{t.age_categories?.join(" · ") || "Open"}</div>
                <h3 className="font-display font-black text-2xl mt-1 tracking-tight group-hover:text-court transition-colors">{t.name}</h3>
                <div className="mt-3 space-y-1.5 text-sm text-zinc-400">
                  <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" />{t.location}</div>
                  <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5" />{t.start_date} → {t.end_date}</div>
                </div>
                <div className="mt-4 pt-4 border-t border-zinc-900 flex items-center justify-between">
                  <span className="text-xs font-mono text-zinc-500">Entry ${t.entry_fee}</span>
                  <span className="text-xs font-bold tracking-widest uppercase text-zinc-300 group-hover:text-court">Details →</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
